import imageCompression from 'browser-image-compression';
import exifr from 'exifr';

export type PhotoAngle = 'front' | 'left' | 'right' | 'unspecified';

export interface PhotoMetadata {
  width: number;
  height: number;
  sizeKB: number;
  mime: string;
  hash: string;
  brightness?: number;
  facesDetected?: number;
}

export interface PhotoValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  originalWidth?: number;
  originalHeight?: number;
}

export interface ProcessedPhoto {
  file: File;
  dataUrl: string;
  metadata: PhotoMetadata;
  thumbnail: {
    blob: Blob;
    dataUrl: string;
    width: number;
    height: number;
  };
  warnings: string[];
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MIN_LONG_EDGE = 1024;
const COMPRESSED_MAX_EDGE = 1600;
const THUMB_MAX_EDGE = 512;

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png'];

export class PhotoValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PhotoValidationError';
  }
}

export const validateImageFile = async (file: File): Promise<PhotoValidationResult> => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    errors.push('Only JPG, JPEG, or PNG images are allowed.');
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    errors.push('Image is too large. Please choose a photo under 10 MB.');
  }

  const { width, height } = await readImageDimensionsFromFile(file);
  const longestEdge = Math.max(width, height);

  if (longestEdge < MIN_LONG_EDGE) {
    errors.push('Image resolution is too low. Please upload at least 1024px on the long side.');
  }

  // Basic brightness hint
  const brightness = await estimateAverageBrightness(file);
  if (brightness !== null && brightness < 50) {
    warnings.push('Lighting looks dim. Try a brighter room for clearer analysis.');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    originalWidth: width,
    originalHeight: height,
  };
};

export const processPhoto = async (file: File): Promise<ProcessedPhoto> => {
  const validation = await validateImageFile(file);
  if (!validation.valid) {
    throw new PhotoValidationError(validation.errors.join(' '));
  }

  const compressed = await imageCompression(file, {
    maxWidthOrHeight: COMPRESSED_MAX_EDGE,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.92,
    alwaysKeepResolution: false,
    preserveExif: false,
  });

  const normalizedFile = ensureFileName(compressed, 'photo.jpg');
  const dataUrl = await blobToDataUrl(normalizedFile);
  const { width, height } = await readImageDimensionsFromFile(normalizedFile);
  const arrayBuffer = await normalizedFile.arrayBuffer();
  const hash = await computeSha256(arrayBuffer);

  const brightness = await estimateAverageBrightness(normalizedFile);
  const facesDetected = await detectFaces(normalizedFile);

  const thumbnail = await generateThumbnail(normalizedFile);

  return {
    file: normalizedFile,
    dataUrl,
    metadata: {
      width,
      height,
      sizeKB: Math.round(normalizedFile.size / 1024),
      mime: normalizedFile.type,
      hash,
      brightness: brightness ?? undefined,
      facesDetected: facesDetected ?? undefined,
    },
    thumbnail,
    warnings: validation.warnings,
  };
};

const ensureFileName = (blob: Blob, fallbackName: string): File => {
  if (blob instanceof File) {
    return blob;
  }
  return new File([blob], fallbackName, { type: blob.type || 'image/jpeg' });
};

const readImageDimensionsFromFile = (file: Blob): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
    };

    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      cleanup();
    };

    img.onerror = (error) => {
      cleanup();
      reject(error);
    };

    img.src = objectUrl;
  });
};

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const computeSha256 = async (buffer: ArrayBuffer): Promise<string> => {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

const generateThumbnail = async (
  file: File,
  maxEdge: number = THUMB_MAX_EDGE
): Promise<{ blob: Blob; dataUrl: string; width: number; height: number }> => {
  const dataUrl = await blobToDataUrl(file);
  const img = await loadImage(dataUrl);

  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to generate thumbnail.');
  }

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const thumbBlob = await canvasToBlob(canvas, 'image/jpeg', 0.8);
  const thumbDataUrl = await blobToDataUrl(thumbBlob);

  return {
    blob: thumbBlob,
    dataUrl: thumbDataUrl,
    width: canvas.width,
    height: canvas.height,
  };
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas conversion failed'));
      } else {
        resolve(blob);
      }
    }, type, quality);
  });
};

const estimateAverageBrightness = async (file: Blob): Promise<number | null> => {
  try {
    const dataUrl = await blobToDataUrl(file);
    const img = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let total = 0;
    const pixels = imageData.data;
    for (let i = 0; i < pixels.length; i += 4 * 10) {
      // sample every 10th pixel for performance
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      total += luminance;
    }
    const samples = pixels.length / (4 * 10);
    return Math.round(total / samples);
  } catch (error) {
    console.warn('Brightness estimation failed:', error);
    return null;
  }
};

const detectFaces = async (file: Blob): Promise<number | null> => {
  if (!('FaceDetector' in window)) {
    return null;
  }

  try {
    const dataUrl = await blobToDataUrl(file);
    const img = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);

    // @ts-ignore FaceDetector is experimental
    const detector = new window.FaceDetector({ fastMode: true });
    const faces = await detector.detect(canvas);
    return faces.length;
  } catch (error) {
    console.warn('Face detection skipped:', error);
    return null;
  }
};

export const readExifOrientation = async (file: File): Promise<number | null> => {
  try {
    const result = await exifr.orientation(file);
    if (typeof result === 'number') {
      return result;
    }
    return null;
  } catch (error) {
    console.warn('EXIF orientation read failed:', error);
    return null;
  }
};


